import type { NostrEvent } from '@nostrify/nostrify';
import { Card, CardContent } from '@/components/ui/card';
import { extractImageUrls } from '@/hooks/usePictures';
import { useState } from 'react';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';

interface PictureCardProps {
  event: NostrEvent;
}

export function PictureCard({ event }: PictureCardProps) {
  const imageUrls = extractImageUrls(event);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const titleTag = event.tags.find(([name]) => name === 'title');
  const title = titleTag?.[1];

  if (imageUrls.length === 0) return null;

  return (
    <>
      <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 group">
        <CardContent className="p-0">
          <div
            className={`grid gap-1 ${
              imageUrls.length === 1
                ? 'grid-cols-1'
                : imageUrls.length === 2
                ? 'grid-cols-2'
                : imageUrls.length === 3
                ? 'grid-cols-3'
                : 'grid-cols-2'
            }`}
          >
            {imageUrls.slice(0, 4).map((url, index) => (
              <Dialog key={index}>
                <DialogTrigger asChild>
                  <div
                    className={`relative cursor-pointer overflow-hidden ${
                      imageUrls.length === 3 && index === 0 ? 'col-span-3' : ''
                    } ${imageUrls.length > 4 && index === 3 ? 'relative' : ''}`}
                    onClick={() => setSelectedImage(url)}
                  >
                    <img
                      src={url}
                      alt={title || `Image ${index + 1}`}
                      className="w-full h-64 object-cover transform group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    {imageUrls.length > 4 && index === 3 && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <span className="text-white text-3xl font-bold">
                          +{imageUrls.length - 4}
                        </span>
                      </div>
                    )}
                  </div>
                </DialogTrigger>
              </Dialog>
            ))}
          </div>
          {(title || event.content) && (
            <div className="p-4">
              {title && <h3 className="font-semibold mb-1">{title}</h3>}
              {event.content && (
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {event.content}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={selectedImage !== null} onOpenChange={(open) => !open && setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          {selectedImage && (
            <img
              src={selectedImage}
              alt={title || 'Full size image'}
              className="w-full h-auto"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
